import { Directive, ElementRef, EventEmitter, NgZone, OnDestroy, OnInit, Output } from '@angular/core';

/** How many rows a long list renders at first, and adds each time its end comes near the screen. */
export const RENDER_BATCH = 100;

/** How far below the screen the end of the list may be and still count as reached. */
const LOOKAHEAD = '800px';

/**
 * Put on an empty element after a long list. Emits while that element is on or near the screen, so
 * the host can render the list in batches instead of all at once. After each emit it checks again,
 * so a batch that does not fill the screen asks for the next one.
 *
 * Usage:
 *   <app-row *ngFor="let row of rows | slice:0:shown"></app-row>
 *   <div *ngIf="shown < rows.length" (appRenderMore)="shown = shown + 100"></div>
 */
@Directive({ selector: '[appRenderMore]', standalone: true })
export class RenderMoreDirective implements OnInit, OnDestroy {
  @Output() readonly appRenderMore = new EventEmitter<void>();

  private observer?: IntersectionObserver;
  private recheck?: ReturnType<typeof setTimeout>;

  constructor(private elementRef: ElementRef<HTMLElement>, private ngZone: NgZone) {}

  ngOnInit(): void {
    this.ngZone.runOutsideAngular(() => {
      this.observer = new IntersectionObserver(entries => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        this.ngZone.run(() => this.appRenderMore.emit());
        // Observing afresh reports the current position, so a sentinel still on screen emits again.
        this.recheck = setTimeout(() => {
          const element = this.elementRef.nativeElement;
          this.observer?.unobserve(element);
          this.observer?.observe(element);
        }, 50);
      }, { rootMargin: `0px 0px ${LOOKAHEAD} 0px` });
      this.observer.observe(this.elementRef.nativeElement);
    });
  }

  ngOnDestroy(): void {
    clearTimeout(this.recheck);
    this.observer?.disconnect();
  }
}
